/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { IconTile } from '@atlaskit/icon';
import Blog24Icon from '@atlaskit/icon-object/glyph/blog/24';
import Page24Icon from '@atlaskit/icon-object/glyph/page/24';
import EditorCloseIcon from '@atlaskit/icon/core/migration/cross--editor-close';
import PlusSquareIcon from '@atlaskit/icon/core/plus-square';
import Image from '@atlaskit/image';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type CuratedDefinitionSourceType } from '../types';

import { messages } from './messages';

const styles = cssMap({
	sourceOptionWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.150'),
		boxSizing: 'border-box',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
			cursor: 'pointer',
		},
	},
	sourceOptionTitle: {
		color: token('color.text'),
		font: token('font.body'),
	},
	sourceOptionMetadata: {
		display: 'flex',
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
	},
	sourceOptionMetatadataSeparator: {
		marginLeft: token('space.050'),
		marginRight: token('space.050'),
	},
	sourceImageContainer: {
		height: '24px',
		width: '24px',
	},
});

type SourceSpaceImageProps = {
	source: CuratedDefinitionSourceType;
};

const SourceSpaceImage = ({ source }: SourceSpaceImageProps) => {
	return source.iconUrl ? (
		<Box xcss={styles.sourceImageContainer}>
			<Image src={source.iconUrl} alt="" />
		</Box>
	) : (
		<IconTile icon={PlusSquareIcon} label="" appearance="blueBold" shape="square" size="24" />
	);
};

type SourceOptionProps = {
	source: CuratedDefinitionSourceType;
	canClear: boolean;
	onClickSource?: () => void;
	onRemoveSource?: (e: React.MouseEvent<HTMLElement>) => void;
};

export const SourceOption = ({
	source,
	canClear,
	onClickSource,
	onRemoveSource,
}: SourceOptionProps) => {
	const { formatDate, formatMessage } = useIntl();

	let sourceIcon = <Page24Icon label="" />;
	if (source.type === 'space') {
		sourceIcon = <SourceSpaceImage source={source} />;
	} else if (source.type === 'blogpost') {
		sourceIcon = <Blog24Icon label="" />;
	}

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box xcss={styles.sourceOptionWrapper} onClick={onClickSource}>
			<Flex alignItems="center" gap="space.150">
				{sourceIcon}
				<Flex direction="column">
					<Box xcss={styles.sourceOptionTitle}>{source.title}</Box>
					<Box xcss={styles.sourceOptionMetadata}>
						<Text>{source.container}</Text>
						{source.lastModified && <Box xcss={styles.sourceOptionMetatadataSeparator}>{`•`}</Box>}
						{source.lastModified && (
							<Text>{`${formatMessage(messages.edit_definition_source_option_metadata_updated_text)} ${formatDate(
								source.lastModified,
								{
									year: 'numeric',
									month: 'short',
									day: '2-digit',
								},
							)}`}</Text>
						)}
					</Box>
				</Flex>
			</Flex>
			{canClear ? (
				<IconButton
					testId={`remove-source-${source.id}`}
					appearance="subtle"
					onClick={onRemoveSource}
					label="remove-source-option-button"
					icon={EditorCloseIcon}
				/>
			) : (
				<div></div>
			)}
		</Box>
	);
};
